import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  DataTable,
  EmptyState,
  Icon,
  Pagination,
  Panel,
  Stat,
  Tag,
  useErrorDialog,
  type Column,
} from '@dayon/design-system';
import { formatCount, formatPesos } from '@dayon/money';
import type { PageMeta, TransactionRow } from '@dayon/contracts';
import { api, ApiRequestError } from '../../../lib/api.ts';
import { STAMP } from '../format.ts';

/**
 * This player's ledger.
 *
 * The tab used to render "The ledger is not on this route" over an endpoint
 * that has always answered: `GET /api/transactions?playerId=…` is the same read
 * the roster's quick look makes, and it is player-scoped by the route rather
 * than filtered in the page. The claim was simply out of date, and a false
 * notice is worse than an empty table because an operator stops looking.
 *
 * It is NOT the same read as the profile's own — it is paged independently, so
 * a player with two thousand rows does not have to be loaded whole to show the
 * first eight of them.
 *
 * WHAT IT NEEDS. `/api/transactions` sits behind `transactions.view`, which the
 * profile itself does not require — a Support Agent holds players.view and not
 * this. So the capability is checked before the read rather than after the 403,
 * and the reason is stated instead of an error modal the operator cannot act on.
 */
const PAGE_SIZE = 8;

const KIND_LABEL: Record<string, string> = {
  DEPOSIT: 'Deposit',
  WITHDRAWAL: 'Withdrawal',
  BET: 'Bet',
  WIN: 'Win',
  BONUS_GRANT: 'Bonus grant',
  BONUS_CONVERT: 'Bonus converted',
  BONUS_FORFEIT: 'Bonus forfeit',
  ADJUSTMENT: 'Adjustment',
  COMMISSION: 'Commission',
  REVERSAL: 'Reversal',
};

const columns: Column<TransactionRow>[] = [
  {
    key: 'ref',
    header: 'Transaction',
    render: (t) => (
      <>
        <span className="tabular">{t.ref}</span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {STAMP.format(new Date(t.occurredAt))}
        </div>
      </>
    ),
  },
  {
    key: 'kind',
    header: 'Type',
    render: (t) => <Tag tone="neutral">{KIND_LABEL[t.kind] ?? t.kind}</Tag>,
  },
  {
    key: 'status',
    header: 'Status',
    // "Failed and Reversed always carry a provider error code" — the database
    // CHECK enforces it, so the code is printed with the status.
    render: (t) => (
      <>
        <Tag
          tone={
            t.status === 'SETTLED' ? 'accent' : t.status === 'FAILED' ? 'quiet' : 'outline'
          }
        >
          {t.status.toLowerCase().replace(/_/g, ' ')}
        </Tag>
        {t.providerErrorCode ? (
          <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
            {t.providerErrorCode}
          </div>
        ) : null}
      </>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    // Signed against the PLAYER's balance by the route, so a deposit credits.
    render: (t) => <span className="tabular">{formatPesos(BigInt(t.amount), { sign: true })}</span>,
  },
];

export function TransactionsTab({
  playerId,
  playerRef,
  canViewLedger,
  role,
}: {
  playerId: string;
  playerRef: string;
  canViewLedger: boolean;
  role: string;
}) {
  const { showError } = useErrorDialog();
  const [rows, setRows] = useState<TransactionRow[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  /* "The read failed" and "there are none" are different facts, and on a money
     screen only one of them is safe to act on. */
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canViewLedger) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      playerId,
      page: String(page),
      perPage: String(PAGE_SIZE),
    });
    api
      .get<{ items: TransactionRow[]; meta: PageMeta }>(`/api/transactions?${params}`)
      .then((d) => {
        if (cancelled) return;
        setRows(d.items);
        setMeta(d.meta);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setRows([]);
        setMeta(null);
        setFailed(true);
        showError({
          title: 'Could not load this player’s ledger',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The read did not complete. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [playerId, page, canViewLedger, showError]);

  if (!canViewLedger) {
    return (
      <Panel title="Transactions on this account">
        <EmptyState icon="lock-key" title="The ledger needs its own capability">
          Reading the financial ledger is <span className="tabular">transactions.view</span>, and
          this profile only requires <span className="tabular">players.view</span> — you are {role},
          so the rows are not fetched rather than fetched and refused. This is not a statement about
          whether the player has transactions.
        </EmptyState>
      </Panel>
    );
  }

  /* Summed from the rows ON THIS PAGE, and labelled as such. The endpoint
     returns no aggregate over the whole filter, and a total that looked like a
     lifetime figure but described eight rows would be read as the first. */
  let credited = 0n;
  let debited = 0n;
  for (const r of rows) {
    const v = BigInt(r.amount);
    if (v >= 0n) credited += v;
    else debited -= v;
  }

  return (
    <Panel
      title="Transactions on this account"
      actions={
        /* Same key as the rows below it: the Transactions module refuses anyone
           without `transactions.view`, so offering the link to a role that has
           just been told it cannot read the ledger here would send them to a
           second locked screen to be told again. */
        canViewLedger ? (
          <Link
            className="btn btn-ghost btn-sm"
            to={`/transactions?playerId=${playerId}&playerRef=${encodeURIComponent(playerRef)}`}
          >
            Open in Transactions
            <Icon name="caret-right" />
          </Link>
        ) : null
      }
    >
      {loading && rows.length === 0 ? (
        <div className="skeleton" style={{ height: '240px' }}>
          &nbsp;
        </div>
      ) : (
        <DataTable
          caption={`Ledger transactions for ${playerRef}, newest first`}
          columns={columns}
          rows={rows}
          rowKey={(t) => t.id}
          empty={
            <EmptyState
              icon={failed ? 'warning-circle' : 'arrows-left-right'}
              title={failed ? 'The ledger could not be read' : 'No transactions on this account'}
            >
              {failed
                ? 'This table is empty because the request failed, not because the ledger holds nothing for this player. Nothing here is a count of anything.'
                : `A real zero — the ledger was read and holds no transactions for ${playerRef}. A deposit that FAILED or is still PENDING writes no ledger transaction at all, so an attempt is not a row here.`}
            </EmptyState>
          }
        />
      )}

      {rows.length > 0 && (
        <>
          <div className="stat-grid" style={{ marginTop: 'var(--space-4)' }}>
            <Stat label="Credited on this page" value={formatPesos(credited)} />
            <Stat label="Debited on this page" value={formatPesos(debited)} />
            <Stat
              label="Rows in this ledger"
              value={formatCount(meta?.total ?? rows.length)}
              delta="every kind — deposits, bets, wins, withdrawals, bonuses and adjustments"
            />
          </div>
          <div className="text-subtle" style={{ fontSize: '11px', marginTop: 'var(--space-2)' }}>
            The two money figures are the {rows.length} row{rows.length === 1 ? '' : 's'} on this
            page, not a lifetime total — no total across the whole filter comes back with the list.
            The lifetime deposit, withdrawal and GGR figures are the tiles at the top of this
            profile, worked out from every settled row.
          </div>
        </>
      )}

      {meta && (
        <Pagination page={meta.page} perPage={meta.perPage} total={meta.total} onPage={setPage} />
      )}

      <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
        <Icon name="info" />
        <div>
          There is no <strong>Rail / source</strong> column: what this list can
          read carries the reference, kind, status, provider error code, signed amount, player and
          brand, but never the rail — resolving one needs a lookup across{' '}
          payment methods — resolving a rail needs a lookup
          across deposits,{' '}
          withdrawals,{' '}
          bonus grants and{' '}
          manual adjustments. The order is fixed to newest
          first, so this table is not sortable. <strong>Open in Transactions</strong> now carries
          this player into that module as a real filter, so the view it opens is the one described
          here and can be shared as a link.
        </div>
      </div>
    </Panel>
  );
}
